import { debounce as _debounce, throttle as _throttle } from "lodash";
import "intersection-observer";

import { convertToRange } from '../../lib/maths';
import { isLowGradeDevice, getScrollTop } from '../../lib/helpers';

const EASE_TRANSLATE = 0.033;
const EASE_ROTATE = 0.033;
const MIN = -60;
const MAX = 60;

const ParalaxElement = el => {
  if (isLowGradeDevice()) return;

  el.style.willChange = 'transform';
  el.style.transformOrigin = 'center center';

  const scrollViewport = document.querySelector('.scroll-viewport') || document;

  let isInView = null;
  let ignoreScroll = false;
  let offsetTop = 0;
  let clientHeight = el.clientHeight;
  let viewportHeight = window.innerHeight;

  let targetTranslate = 0;
  let currentTranslate = 0;
  let targetRotate = 0;
  let currentRotate = 0;

  let raf, then, now, correction;

  const speed = 0.8 + (Math.random() * 0.5);
  const direction = Math.random() > 0.5 ? 1 : -1;
  const rotateDirection = Math.random() > 0.5 ? 1 : -1;

  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 0,
  }

  const animate = () => {
    then = now ? now : null;
    now = new Date().getTime();
    correction = then ? (now - then) / 16.666 : 1;
    correction = Math.min(correction, 2);
  
    if (Math.abs(targetTranslate - currentTranslate) > 0.2) {
      currentTranslate += (targetTranslate - currentTranslate) * (EASE_TRANSLATE * speed * correction);
    } else {
      targetTranslate = currentTranslate;
    }
    currentRotate += (targetRotate - currentRotate) * (EASE_ROTATE * speed * correction);

    el.style.transform = `translateY(${currentTranslate}px) rotate(${currentRotate}deg)`;
    raf = requestAnimationFrame(animate);
  }

  const onResize = _debounce(() => {
    viewportHeight = window.innerHeight;
    offsetTop = el.getBoundingClientRect().top + getScrollTop(scrollViewport);
    clientHeight = el.clientHeight;
  }, 333);
  

  const onScroll = e => {
    if (ignoreScroll) return;
    ignoreScroll = true;
    requestAnimationFrame(() => ignoreScroll = false);

    const scrollTop = getScrollTop(e.target);
    const min = offsetTop - viewportHeight;
    const max = offsetTop + clientHeight;

    targetTranslate = convertToRange(scrollTop, [min, max], [MIN * direction, MAX * direction]);
    targetRotate = convertToRange(scrollTop, [min, max], [(-5 * rotateDirection * direction), (5 * rotateDirection * direction)]);
  };

  const onInView = () => {
    if (isInView === true) return;
    isInView = true;
    
    window.addEventListener('scroll', onScroll);
    scrollViewport.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    
    then = undefined;
    now = undefined;
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(animate);
  }

  const onExitView = () => {
    if (isInView === false) return;
    isInView = false;
    window.removeEventListener('scroll', onScroll);
    scrollViewport.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);

    then = undefined;
    now = undefined;
    cancelAnimationFrame(raf);
  }

  const observerCallback = entries => {
    const entry = entries[0];

    if (entry.isIntersecting) {
        onInView();
    } else {
        onExitView();
    }
  }

  const onFocus = () => {
    if (isInView) {
      then = undefined;
      now = undefined;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(animate);
    }
  }

  const onBlur = () => {
    then = undefined;
    now = undefined;
    cancelAnimationFrame(raf);
  }

  offsetTop = el.getBoundingClientRect().top + getScrollTop(scrollViewport);
  const observer = new IntersectionObserver(observerCallback, options);
  observer.observe(el);
  window.addEventListener('focus', onFocus);
  window.addEventListener('blur', onBlur);
};

export default ParalaxElement;
